// ╔════════════════════════════════════════════════════════════════════════════╗
// ║  HOMETAB.JSX - VERSION: 2025.12.30.2330                                    ║
// ╚════════════════════════════════════════════════════════════════════════════╝
// ============================================================================
// 📦 VERSION: 2025.12.30.2330
// ============================================================================
// CHANGELOG:
// 2025.12.23.1500 - UI: Redesigned Solo/Group selector with unified timer + multipliers
// 2025.12.18.2100 - PERF: Lazy loading + loading animation for quiz pictures
// 2025.12.18.2100 - UI: Larger logo (h-20/h-24), better mobile sizing
// 2025.12.16.0100 - UI: Larger logo (h-16), added top padding for login button
// 2025.12.15.1910 - Use full TRAKtag logo instead of icon/bulb
// 2025.12.15.1845 - FIX: Group Quiz - don't call onStartGame after onStartGroupMode
// 2025.12.13.1430 - Added "How to Play" link to footer
// 2025.12.12.1900 - Added legal footer links (Terms, Privacy, GDPR) from App Settings
// 2025.12.12.1845 - UX: Right justified name, ellipsis truncate, popularity rating, shimmer animation
// 2025.12.12.1745 - UX: Full height pic (96px), no text overlay, name+stars on right only
// 2025.12.12.1745 - UX: Full height pic (96px), no text overlay, name+stars on right only
// 2025.12.12.1730 - UX: Larger pics (80px), star rating, "Suggest a Quiz" text link
// 2025.12.12.1715 - UX: Horizontal quiz rows with small pic left, more text space right
// 2025.12.12.1700 - UX REDESIGN: Replaced Group/Solo buttons with 2 quiz cards + mode modal
// 2025.12.04.0001 - FIX: Player profile only shows when isAuthenticated=true (not when logged out)
// 2025.12.02.1345 - Fixed picture display for database (snake_case columns)
// 2025.11.30.0145 - Favicon fallback instead of emoji for Featured Quiz
// 2025.11.29.0130 - Email mandatory in Suggest Quiz, onStartSoloMode prop
// 2025.11.29.0030 - Added Suggest a Quiz button
// 2025.11.29.0025 - Featured Quiz display, Tell a Friend sharing
// 2025.11.28.XXXX - Home page redesign with Group Quiz & Solo Player buttons
// ============================================================================
import React, { useState, useEffect } from 'react';

const HomeTab = ({ 
  playerData,
  isAuthenticated = false,  // 🔧 FIX 2025.12.04: Only show profile when authenticated
  games = [],
  pictures = [],
  selectedGameId,
  onSelectGame,
  onStartGame,
  onGoToQuizzes,
  onStartGroupMode,
  onStartSoloMode,  // 🆕 NEW: Ensures group mode is OFF
  onSelectMode,     // 🆕 NEW: For setting speed mode
  groupQuizTimer,   // 🆕 NEW: Current group timer
  setGroupQuizTimer, // 🆕 NEW: For setting group timer
  logoUrl,
  iconUrl
}) => {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [showModePopup, setShowModePopup] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showSuggestPopup, setShowSuggestPopup] = useState(false);
  const [suggestionData, setSuggestionData] = useState({ suggestion: '', email: '' });
  const [suggestionSent, setSuggestionSent] = useState(false);
  
  // 🆕 NEW: App settings for footer links
  const [appSettings, setAppSettings] = useState({
    termsUrl: 'https://traktag.com/terms',
    privacyUrl: 'https://traktag.com/privacy',
    gdprUrl: 'https://traktag.com/gdpr',
    appName: 'TRAKtag',
    copyrightYear: '2025'
  });
  
  // Load app settings from localStorage
  useEffect(() => {
    try {
      const stored = localStorage.getItem('trakquiz_app_settings');
      if (stored) {
        setAppSettings(JSON.parse(stored));
      }
    } catch (e) {
      console.log('Using default app settings');
    }
  }, []);
  
  // 🆕 NEW: Quiz selection modal state
  const [selectedQuizForModal, setSelectedQuizForModal] = useState(null);
  const [showQuizModal, setShowQuizModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState(null); // 'solo' or 'group'
  const [selectedSpeed, setSelectedSpeed] = useState(() => {
    return localStorage.getItem('lastSoloSpeed') || 'relaxed';
  });
  const [selectedTimer, setSelectedTimer] = useState(() => {
    const saved = localStorage.getItem('lastGroupTimer');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  
  // Speed modes for Solo play
  const speedModes = [
    { id: 'relaxed', name: 'Scenic', icon: '🚶', timer: 'No timer', multiplier: '1x', color: 'from-green-500 to-emerald-600' },
    { id: 'thinker', name: 'Local', icon: '🚌', timer: '15 sec', multiplier: '1.25x', color: 'from-blue-500 to-cyan-600' },
    { id: 'speed', name: 'Express', icon: '🚄', timer: '10 sec', multiplier: '1.5x', color: 'from-orange-500 to-amber-600' },
    { id: 'lightning', name: 'Bullet', icon: '🚀', timer: '6 sec', multiplier: '2x', color: 'from-red-500 to-rose-600' }
  ];

  // Unified timer options (for both Solo and Group) with multipliers
  const timerOptions = [
    { value: 0, label: 'NONE', multiplier: '1x' },
    { value: 10, label: '10s', multiplier: '1.5x' },
    { value: 15, label: '15s', multiplier: '1.25x' },
    { value: 25, label: '25s', multiplier: '1.1x' },
    { value: 35, label: '35s', multiplier: '1x' }
  ];

  // Group Quiz timer options (legacy - keeping for compatibility)
  const groupTimerOptions = [
    { value: 0, label: 'NONE', color: 'from-gray-500 to-gray-600' },
    { value: 10, label: '10s', color: 'from-green-500 to-emerald-600' },
    { value: 15, label: '15s', color: 'from-blue-500 to-cyan-600' },
    { value: 25, label: '25s', color: 'from-orange-500 to-amber-600' },
    { value: 35, label: '35s', color: 'from-red-500 to-rose-600' }
  ];
  
  // Active games only - exclude parent categories (they only show in Quizzes tab)
  const activeGames = games.filter(g => g.active && !g.is_parent);
  
  // Find featured quiz (game.featured = true) or use rotation
  const featuredQuiz = activeGames.find(g => g.featured);
  const displayedFeaturedQuiz = featuredQuiz || activeGames[featuredIndex] || null;
  
  // Find featured image for the displayed quiz
  const getFeaturedImage = (gameId) => {
    if (!gameId) return null;
    const gamePictures = pictures.filter(p => p.game_id === gameId);
    
    // First try to find one marked as featured_image (database) or featuredImage (legacy)
    const featured = gamePictures.find(p => p.featuredImage || p.featured_image);
    if (featured) return featured.image_url || featured.file_path || featured.url || featured.imageUrl || featured.src;
    
    // Otherwise use first active picture
    const firstActive = gamePictures.find(p => p.active !== false);
    if (firstActive) return firstActive.image_url || firstActive.file_path || firstActive.url || firstActive.imageUrl || firstActive.src;
    
    // Try first picture regardless
    if (gamePictures.length > 0) {
      return gamePictures[0].image_url || gamePictures[0].file_path || gamePictures[0].url || gamePictures[0].imageUrl || gamePictures[0].src;
    }
    
    return null;
  };
  
  const featuredImage = displayedFeaturedQuiz ? getFeaturedImage(displayedFeaturedQuiz.game_id) : null;
  
  // Get player achievement level
  const getAchievementLevel = (traks) => {
    const levels = [
      { min: 0, name: 'Newcomer', icon: '🌱' },
      { min: 100, name: 'Bronze', icon: '🥉' },
      { min: 250, name: 'Silver', icon: '🥈' },
      { min: 500, name: 'Gold', icon: '🥇' },
      { min: 1000, name: 'Star', icon: '⭐' },
      { min: 2500, name: 'Expert', icon: '🎓' },
      { min: 5000, name: 'Champion', icon: '🏆' },
      { min: 10000, name: 'Legend', icon: '👑' }
    ];
    for (let i = levels.length - 1; i >= 0; i--) {
      if (traks >= levels[i].min) return levels[i];
    }
    return levels[0];
  };
  
  const playerTraks = playerData?.stats?.totalTraks || 0;
  const achievement = getAchievementLevel(playerTraks);
  
  // Rotate featured quiz every 30 seconds if none specifically set
  useEffect(() => {
    if (!featuredQuiz && activeGames.length > 1) {
      const interval = setInterval(() => {
        setFeaturedIndex(prev => (prev + 1) % activeGames.length);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [featuredQuiz, activeGames.length]);

  // 🆕 Open quiz modal for any quiz
  const handleQuizCardClick = (quiz) => {
    onSelectGame(quiz.game_id);
    setSelectedQuizForModal(quiz);
    setSelectedTab(null);
    setShowQuizModal(true);
  };

  // Show mode selection popup (updated to use new modal)
  const handlePlayFeatured = () => {
    if (displayedFeaturedQuiz) {
      handleQuizCardClick(displayedFeaturedQuiz);
    }
  };

  // 🆕 Handle speed selection
  const handleSpeedSelect = (speedId) => {
    setSelectedSpeed(speedId);
    localStorage.setItem('lastSoloSpeed', speedId);
  };

  // 🆕 Handle timer selection
  const handleTimerSelect = (timerValue) => {
    setSelectedTimer(timerValue);
    localStorage.setItem('lastGroupTimer', timerValue.toString());
  };

  // 🆕 Start Solo Game from modal - Fixed 2025.12.24
  const handleStartSoloFromModal = () => {
    if (onSelectMode) onSelectMode(selectedSpeed);
    if (onStartSoloMode) onStartSoloMode();
    setShowQuizModal(false);
    onStartGame();
  };

  // 🆕 Start Group Game from modal
  // 🔧 FIX 2025.12.14: Don't call onStartGame after onStartGroupMode (it overwrites groupQuizMode)
  const handleStartGroupFromModal = () => {
    if (setGroupQuizTimer) setGroupQuizTimer(selectedTimer);
    setShowQuizModal(false);
    if (onStartGroupMode) onStartGroupMode();  // This should set mode AND start game
  };

  // 🆕 Get 2 random quizzes for display (excluding featured)
  const getRandomQuizzes = () => {
    const otherQuizzes = activeGames.filter(g => g.game_id !== displayedFeaturedQuiz?.game_id);
    const shuffled = [...otherQuizzes].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  };
  
  const randomQuizzes = getRandomQuizzes();

  // Start Solo Player mode (legacy - keeping for compatibility)
  const handleStartSolo = () => {
    setShowModePopup(false);
    onStartGame();
  };

  // Start Group Quiz mode (legacy - keeping for compatibility)
  const handleStartGroup = () => {
    setShowModePopup(false);
    if (onStartGroupMode) onStartGroupMode();
    onGoToQuizzes();
  };

  // Tell a Friend share functionality
  const shareMessage = "I am playing the new TRAKtag quiz. Download your own app and join me to play at www.TRAKtag.com";
  
  const handleShare = (platform) => {
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, '_blank');
        setShowShareMenu(false);
        break;
      case 'copy':
        navigator.clipboard.writeText(shareMessage);
        setLinkCopied(true);
        setTimeout(() => {
          setLinkCopied(false);
          setShowShareMenu(false);
        }, 3000);
        break;
      default:
        break;
    }
  };

  // Handle quiz suggestion submission
  const handleSendSuggestion = () => {
    if (!suggestionData.suggestion.trim()) {
      alert('Please enter your quiz suggestion');
      return;
    }
    if (!suggestionData.email.trim()) {
      alert('Please enter your email so we can contact you when your quiz is created');
      return;
    }
    
    // Save suggestion to localStorage
    const suggestions = JSON.parse(localStorage.getItem('quizSuggestions') || '[]');
    suggestions.push({
      id: Date.now(),
      suggestion: suggestionData.suggestion,
      email: suggestionData.email,
      odeName: playerData?.name || 'Anonymous',
      oderId: playerData?.oderId || null,
      timestamp: new Date().toISOString()
    });
    localStorage.setItem('quizSuggestions', JSON.stringify(suggestions));
    
    setSuggestionSent(true);
    setTimeout(() => {
      setSuggestionSent(false);
      setShowSuggestPopup(false);
      setSuggestionData({ suggestion: '', email: '' });
    }, 2000);
  };

  return (
    <div className="min-h-screen bg-gray-900 pb-20">
      <div className="max-w-md mx-auto px-4 pt-14 pb-4">
        
        {/* Header with Full TRAKtag Logo - larger size */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <img 
                src={logoUrl} 
                alt="TRAKtag" 
                className="h-20 md:h-24"
                style={{ filter: 'drop-shadow(0 2px 8px rgba(255, 200, 0, 0.4))' }}
              />
            ) : (
              <h1 className="text-4xl md:text-5xl font-black">
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-500 to-blue-500">TRAK</span>
                <span className="text-white">tag</span>
              </h1>
            )}
          </div>
          
          {/* Player Stats - only show when logged in (below login button) */}
          {playerData?.name && isAuthenticated && (
            <div className="text-right mt-2">
              <p className="text-yellow-400 text-sm">{achievement.icon} {achievement.name}</p>
              <p className="text-blue-300 text-xs">🏆 {playerTraks.toLocaleString()} TRAKs</p>
            </div>
          )}
        </div>

        {/* Featured Quiz Box */}
        {displayedFeaturedQuiz && (
          <div className="bg-gray-800 rounded-xl p-4 mb-4 border-2 border-yellow-500/50">
            {/* Header */}
            <div className="flex items-center gap-2 mb-3">
              <span className="text-yellow-400">⭐</span>
              <span className="text-yellow-300 font-bold text-sm">Featured Quiz</span>
              {!featuredQuiz && activeGames.length > 1 && (
                <span className="ml-auto text-gray-500 text-xs">🔄 Rotating</span>
              )}
            </div>
            
            {/* Quiz Content - Picture on left, Info on right */}
            <div className="flex gap-4">
              {/* Quiz Picture */}
              <div className="w-24 h-24 flex-shrink-0 rounded-lg overflow-hidden bg-gray-700 animate-pulse">
                {featuredImage ? (
                  <img 
                    src={featuredImage} 
                    alt={displayedFeaturedQuiz.name}
                    className="w-full h-full object-cover"
                    loading="eager"
                    fetchpriority="high"
                    onLoad={(e) => e.target.parentElement.classList.remove('animate-pulse')}
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.classList.remove('animate-pulse');
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-600">
                    <img src="/favicon-48.png" alt="Quiz" className="w-12 h-12" onError={(e) => e.target.style.display='none'} />
                  </div>
                )}
              </div>
              
              {/* Quiz Info */}
              <div className="flex-1 flex flex-col">
                <h3 className="text-white font-bold text-lg">
                  {displayedFeaturedQuiz.name}
                </h3>
                <p className="text-gray-400 text-sm mt-1 flex-1">
                  {displayedFeaturedQuiz.description || 'Test your knowledge!'}
                </p>
                <button
                  onClick={handlePlayFeatured}
                  className="bg-gradient-to-r from-green-500 to-emerald-600 text-white font-bold py-2 px-4 rounded-lg text-sm mt-2 hover:from-green-400 hover:to-emerald-500 transition-all"
                >
                  ▶️ Play Now
                </button>
              </div>
            </div>
            
            {/* Click for Full Quiz List */}
            <button
              onClick={onGoToQuizzes}
              className="w-full mt-3 text-blue-400 hover:text-blue-300 text-sm underline"
            >
              Click for Full Quiz List
            </button>
          </div>
        )}

        {/* 🆕 Two Quick Quiz Cards - Full height pics, name on right */}
        {randomQuizzes.length > 0 && (
          <div className="mb-4">
            {/* Header row with Browse link */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-orange-400">🔥</span>
                <span className="text-orange-300 font-bold text-sm">More Quizzes</span>
              </div>
              <button
                onClick={onGoToQuizzes}
                className="text-blue-400 hover:text-blue-300 text-sm font-medium"
              >
                Browse All Quizzes →
              </button>
            </div>
            
            <div className="space-y-3">
              {randomQuizzes.map(quiz => {
                const quizImage = getFeaturedImage(quiz.game_id);
                
                return (
                  <button
                    key={quiz.game_id}
                    onClick={() => handleQuizCardClick(quiz)}
                    className="w-full bg-gray-800 rounded-xl overflow-hidden border-2 border-gray-700 hover:border-blue-500 transition-all flex items-stretch"
                  >
                    {/* Full Height Square Picture - no text */}
                    <div className="w-24 h-24 flex-shrink-0 bg-gray-700 animate-pulse">
                      {quizImage ? (
                        <img 
                          src={quizImage} 
                          alt={quiz.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onLoad={(e) => e.target.parentElement.classList.remove('animate-pulse')}
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.classList.remove('animate-pulse');
                          }}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-blue-600 to-teal-600">
                          <span className="text-3xl">🎯</span>
                        </div>
                      )}
                    </div>
                    {/* Quiz Name + Popularity Rating - RIGHT JUSTIFIED */}
                    <div className="flex-1 flex flex-col justify-center px-4 text-right">
                      <p className="text-white font-bold text-base mb-1 truncate">{quiz.name}</p>
                      <p className="text-yellow-400 text-xs">Popularity: ⭐⭐⭐⭐☆</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Tell a Friend Button */}
        <div className="relative mb-4">
          <button
            onClick={() => setShowShareMenu(!showShareMenu)}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-4 text-center hover:scale-105 transition-transform flex items-center justify-center gap-2"
          >
            <span className="text-2xl">📨</span>
            <span className="text-white font-bold">Tell a Friend</span>
          </button>
          
          {/* Share Menu Dropdown */}
          {showShareMenu && (
            <div className="absolute top-full left-0 right-0 mt-2 bg-gray-800 rounded-xl p-4 shadow-xl border border-gray-700 z-50">
              {linkCopied ? (
                <div className="text-center">
                  <p className="text-green-400 font-bold mb-2">✅ Link copied to clipboard!</p>
                  <p className="text-gray-300 text-sm">Email to friends or post on Social Media!</p>
                </div>
              ) : (
                <>
                  <p className="text-gray-300 text-sm mb-3 text-center">
                    Share TRAKtag with your friends:
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleShare('whatsapp')}
                      className="flex-1 bg-green-600 hover:bg-green-500 text-white py-3 px-3 rounded-lg text-sm font-bold"
                    >
                      📱 WhatsApp
                    </button>
                    <button
                      onClick={() => handleShare('copy')}
                      className="flex-1 bg-blue-600 hover:bg-blue-500 text-white py-3 px-3 rounded-lg text-sm font-bold"
                    >
                      📋 Copy Link
                    </button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Suggest a Quiz - Text link */}
        <div className="text-center mt-4 mb-2">
          <button
            onClick={() => setShowSuggestPopup(true)}
            className="text-gray-400 hover:text-blue-400 text-sm underline transition-colors"
          >
            💡 Suggest a Quiz
          </button>
        </div>

        {/* Legal Footer Links */}
        <div className="text-center mt-4 mb-6 pt-4 border-t border-gray-800">
          <div className="flex justify-center items-center gap-2 text-xs text-gray-500 mb-2">
            <a 
              href="/help" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-yellow-500 hover:text-yellow-400 transition-colors font-bold"
            >
              📖 How to Play
            </a>
          </div>
          <div className="flex justify-center items-center gap-3 text-xs text-gray-500">
            <a 
              href={appSettings.termsUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
            >
              Terms
            </a>
            <span>|</span>
            <a 
              href={appSettings.privacyUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
            >
              Privacy
            </a>
            <span>|</span>
            <a 
              href={appSettings.gdprUrl} 
              target="_blank" 
              rel="noopener noreferrer"
              className="hover:text-blue-400 transition-colors"
            >
              GDPR
            </a>
          </div>
          <p className="text-gray-600 text-xs mt-2">© {appSettings.copyrightYear} {appSettings.appName}. All rights reserved.</p>
        </div>

        {/* Mode Selection Popup */}
        {showModePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 max-w-sm w-full shadow-2xl border border-blue-500">
              <h3 className="text-white text-xl font-bold mb-2 text-center">
                {displayedFeaturedQuiz?.featuredEmoji || '🎯'} {displayedFeaturedQuiz?.name}
              </h3>
              <p className="text-gray-400 text-sm mb-4 text-center">
                How would you like to play?
              </p>
              
              <div className="space-y-3">
                <button
                  onClick={handleStartGroup}
                  className="w-full bg-gradient-to-r from-blue-600 to-teal-600 text-white font-bold py-4 px-6 rounded-xl text-lg hover:from-blue-500 hover:to-teal-500 transition-all flex items-center justify-center gap-3"
                >
                  <span className="text-2xl">👥</span>
                  <span>Group Quiz</span>
                </button>
                
                <button
                  onClick={handleStartSolo}
                  className="w-full bg-gradient-to-r from-green-600 to-emerald-600 text-white font-bold py-4 px-6 rounded-xl text-lg hover:from-green-500 hover:to-emerald-500 transition-all flex items-center justify-center gap-3"
                >
                  <span className="text-2xl">🎮</span>
                  <span>Solo Player</span>
                </button>
              </div>
              
              <button
                onClick={() => setShowModePopup(false)}
                className="w-full mt-4 text-gray-400 hover:text-white text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Suggest a Quiz Popup */}
        {showSuggestPopup && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4">
            <div className="bg-gray-800 rounded-xl p-6 max-w-sm w-full shadow-2xl border border-yellow-500">
              {suggestionSent ? (
                <div className="text-center py-4">
                  <span className="text-5xl block mb-3">✅</span>
                  <p className="text-green-400 font-bold text-lg">Thank you!</p>
                  <p className="text-gray-300 text-sm">Your suggestion has been sent.</p>
                </div>
              ) : (
                <>
                  <h3 className="text-white text-xl font-bold mb-4 text-center flex items-center justify-center gap-2">
                    <span>💡</span> Suggest a Quiz
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Quiz Suggestion:</label>
                      <textarea
                        value={suggestionData.suggestion}
                        onChange={(e) => setSuggestionData({...suggestionData, suggestion: e.target.value})}
                        placeholder="What quiz would you like to see?"
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm resize-none"
                        rows={3}
                      />
                    </div>
                    
                    <div>
                      <label className="text-gray-300 text-sm block mb-1">Your Email (so we can contact you when your Quiz is created):</label>
                      <input
                        type="email"
                        value={suggestionData.email}
                        onChange={(e) => setSuggestionData({...suggestionData, email: e.target.value})}
                        placeholder="your@email.com"
                        className="w-full bg-gray-700 text-white px-3 py-2 rounded-lg text-sm"
                        required
                      />
                    </div>
                    
                    <button
                      onClick={handleSendSuggestion}
                      className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-3 px-6 rounded-xl hover:from-yellow-400 hover:to-orange-400 transition-all"
                    >
                      📤 Send Suggestion
                    </button>
                  </div>
                  
                  <button
                    onClick={() => {
                      setShowSuggestPopup(false);
                      setSuggestionData({ suggestion: '', email: '' });
                    }}
                    className="w-full mt-3 text-gray-400 hover:text-white text-sm"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        )}

      {/* 🆕 Quiz Selection Modal - REDESIGNED 2025.12.23 */}
      {showQuizModal && selectedQuizForModal && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/80 z-40"
            onClick={() => setShowQuizModal(false)}
          />
          
          {/* Modal */}
          <div className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 max-w-md mx-auto">
            <div className="bg-gradient-to-b from-gray-800 to-gray-900 rounded-2xl border border-blue-500/50 shadow-2xl overflow-hidden">
              
              {/* Header with Quiz Info */}
              <div className="bg-gradient-to-r from-blue-600 to-teal-600 p-4">
                <div className="flex items-center gap-3">
                  {/* Quiz Picture */}
                  <div className="w-14 h-14 flex-shrink-0 rounded-xl overflow-hidden bg-gray-700 shadow-lg">
                    {getFeaturedImage(selectedQuizForModal.game_id) ? (
                      <img 
                        src={getFeaturedImage(selectedQuizForModal.game_id)} 
                        alt={selectedQuizForModal.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-2xl bg-gradient-to-br from-blue-500 to-teal-500">🎯</div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="text-blue-200 text-xs font-medium">Selected Quiz</p>
                    <h2 className="text-white font-bold text-lg">{selectedQuizForModal.name}</h2>
                  </div>
                  <button 
                    onClick={() => setShowQuizModal(false)}
                    className="bg-white/20 hover:bg-white/30 rounded-full w-8 h-8 flex items-center justify-center text-white transition-all"
                  >
                    ✕
                  </button>
                </div>
              </div>

              {/* Modal Content */}
              <div className="p-4">
                {/* Mode Toggle - Pill Style */}
                <div className="flex bg-black/30 rounded-xl p-1 mb-4">
                  <button
                    onClick={() => setSelectedTab('solo')}
                    className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                      selectedTab === 'solo'
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <span>👤</span> SOLO
                  </button>
                  <button
                    onClick={() => setSelectedTab('group')}
                    className={`flex-1 py-3 px-4 rounded-lg font-bold text-sm transition-all flex items-center justify-center gap-2 ${
                      selectedTab === 'group'
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <span>👥</span> GROUP
                  </button>
                </div>

                {/* Timer Selection - Shows for both modes */}
                {selectedTab === 'solo' && (
                  <>
                    <div className="mb-4">
                      <p className="text-gray-400 text-xs font-medium mb-2 flex items-center gap-2">
                        <span>🚂</span> Select Speed:
                      </p>
                      <div className="grid grid-cols-4 gap-2">
                        {speedModes.map(mode => (
                          <button
                            key={mode.id}
                            onClick={() => handleSpeedSelect(mode.id)}
                            className={`p-2 rounded-xl text-center transition-all ${
                              selectedSpeed === mode.id
                                ? `bg-gradient-to-r ${mode.color} text-white ring-2 ring-yellow-400`
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            <div className="text-lg">{mode.icon}</div>
                            <p className="font-bold text-xs">{mode.name}</p>
                            <p className="text-[10px] opacity-75">{mode.multiplier}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-gray-500 text-xs mt-2 text-center">
                        {selectedSpeed === 'relaxed' ? 'No timer - Ask a Pal available!' : speedModes.find(m => m.id === selectedSpeed)?.timer}
                      </p>
                    </div>

                    {/* Start Solo Button */}
                    <button
                      onClick={handleStartSoloFromModal}
                      className="w-full py-4 px-6 rounded-xl font-black text-lg bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500 text-white transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      👤 START SOLO QUIZ
                    </button>
                  </>
                )}

                {selectedTab === 'group' && (
                  <>
                    <div className="mb-4">
                      <p className="text-gray-400 text-xs font-medium mb-2 flex items-center gap-2">
                        <span>⏱️</span> Timer per Question:
                      </p>
                      <div className="flex gap-1.5">
                        {timerOptions.map(option => (
                          <button
                            key={option.value}
                            onClick={() => handleTimerSelect(option.value)}
                            className={`flex-1 py-2.5 px-1 rounded-lg text-center transition-all ${
                              selectedTimer === option.value
                                ? 'bg-green-600 text-white ring-2 ring-green-400 shadow-lg'
                                : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                          >
                            <p className="font-bold text-sm">{option.label}</p>
                            <p className="text-[10px] opacity-70">{option.multiplier}</p>
                          </button>
                        ))}
                      </div>
                      <p className="text-gray-500 text-xs mt-2 text-center">
                        {selectedTimer === 0 ? 'No timer - reveal answer when ready' : `${selectedTimer} seconds per question`}
                      </p>
                    </div>

                    {/* Start Group Button */}
                    <button
                      onClick={handleStartGroupFromModal}
                      className="w-full py-4 px-6 rounded-xl font-black text-lg bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-400 hover:to-emerald-500 text-white transition-all shadow-lg flex items-center justify-center gap-2"
                    >
                      👥 START GROUP QUIZ
                    </button>
                  </>
                )}

                {/* Prompt to select mode if none selected */}
                {!selectedTab && (
                  <div className="text-center py-6">
                    <p className="text-gray-400 text-lg">Select a game mode</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Shimmer animation CSS */}
      <style>{`
        @keyframes shimmer {
          0% {
            background-position: -200% center;
          }
          100% {
            background-position: 200% center;
          }
        }
        
        .shimmer-border {
          position: relative;
          background: linear-gradient(
            90deg,
            transparent 0%,
            rgba(59, 130, 246, 0.3) 25%,
            rgba(20, 184, 166, 0.5) 50%,
            rgba(59, 130, 246, 0.3) 75%,
            transparent 100%
          );
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
          border-radius: 8px;
        }
        
        .shimmer-border::before {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: 8px;
          padding: 2px;
          background: linear-gradient(
            90deg,
            #3b82f6,
            #14b8a6,
            #3b82f6
          );
          -webkit-mask: 
            linear-gradient(#fff 0 0) content-box, 
            linear-gradient(#fff 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          background-size: 200% 100%;
          animation: shimmer 2s ease-in-out infinite;
        }
      `}</style>

      </div>
    </div>
  );
};

export default HomeTab;
